import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import type { PublicConfig } from '@visiward/schemas';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import { Spinner, Alert, Badge } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

const tabs = [
  { to: '/admin/config', label: 'Config' },
  { to: '/admin/users', label: 'Users' },
  { to: '/admin/usage', label: 'Usage' },
  { to: '/admin/contact', label: 'Contact' },
];

export function AdminPage() {
  const location = useLocation();
  const section = location.pathname.split('/').pop() ?? 'config';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-primary">Admin</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Manage launch limits, review usage, and inspect contact requests.
        </p>
      </div>

      <div className="flex gap-1 border-b border-border">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) =>
              cn(
                '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition',
                isActive
                  ? 'border-accent-blue text-text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary',
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </div>

      {section === 'config' && <ConfigSection />}
      {section === 'users' && <ListSection title="Users" fetcher={() => api.admin.listUsers().then((r) => r.users)} />}
      {section === 'usage' && <ListSection title="Daily usage" fetcher={() => api.admin.listUsage().then((r) => r.usage)} />}
      {section === 'contact' && <ListSection title="Contact requests" fetcher={() => api.admin.listContact().then((r) => r.requests)} />}
    </div>
  );
}

function ConfigSection() {
  const { data, isLoading } = useQuery({ queryKey: ['admin-config'], queryFn: () => api.admin.getConfig() });
  const [draft, setDraft] = useState<Partial<PublicConfig>>({});
  const [saved, setSaved] = useState(false);

  const mut = useMutation({
    mutationFn: () => api.admin.updateConfig(draft),
    onSuccess: () => setSaved(true),
  });

  if (isLoading) return <Spinner className="mx-auto my-12" />;
  const config = { ...(data?.config ?? {}), ...draft } as Partial<PublicConfig>;

  const numberField = (key: keyof PublicConfig, label: string) => (
    <div>
      <Label>{label}</Label>
      <Input
        type="number"
        value={String(config[key] ?? '')}
        onChange={(e) => {
          setSaved(false);
          setDraft((d) => ({ ...d, [key]: Number(e.target.value) }));
        }}
      />
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Launch-free limits</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          {numberField('maxAnonymousRunsPerDay', 'Anonymous runs / day')}
          {numberField('maxRegisteredRunsPerDay', 'Registered runs / day')}
          {numberField('maxPromptsPerRun', 'Prompts / run')}
          {numberField('maxProjects', 'Max projects')}
          {numberField('maxCompetitorsPerProject', 'Competitors / project')}
          {numberField('savedHistoryDays', 'Saved history (days)')}
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={config.launchFreeEnabled ?? true}
              onChange={(e) => {
                setSaved(false);
                setDraft((d) => ({ ...d, launchFreeEnabled: e.target.checked }));
              }}
            />
            Launch-free enabled
          </label>
        </div>
        <p className="text-xs text-text-muted">
          Note: advanced analysis availability is controlled by a server-side flag and cannot be
          enabled from here alone.
        </p>
        {saved && <Alert tone="success">Configuration saved.</Alert>}
        <div className="flex justify-end">
          <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? <Spinner /> : 'Save config'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ListSection({
  title,
  fetcher,
}: {
  title: string;
  fetcher: () => Promise<Record<string, unknown>[]>;
}) {
  const { data, isLoading, error } = useQuery({ queryKey: ['admin-list', title], queryFn: fetcher });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {data && <Badge tone="default">{data.length}</Badge>}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Spinner className="mx-auto my-8" />
        ) : error ? (
          <Alert tone="error">Could not load {title.toLowerCase()}.</Alert>
        ) : data && data.length > 0 ? (
          <div className="max-h-[520px] overflow-auto rounded-lg border border-border">
            <pre className="p-4 text-xs text-text-secondary">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-text-muted">Nothing to show.</p>
        )}
      </CardContent>
    </Card>
  );
}
